import React, { createContext, useContext, useEffect, useState } from 'react';
import { safeStorage } from '../utils/safeStorage';

export type FontSizeOption = 'small' | 'medium' | 'large';

export interface FontSizeDetails {
  id: FontSizeOption;
  label: string;
  shortLabel: string;
  sizePx: string;
  description: string;
}

export const FONT_SIZE_OPTIONS: Record<FontSizeOption, FontSizeDetails> = {
  small: {
    id: 'small',
    label: 'Compacto',
    shortLabel: 'A-',
    sizePx: '14px',
    description: 'Mais itens na tela, ideal para telas menores'
  },
  medium: {
    id: 'medium',
    label: 'Padrão',
    shortLabel: 'A',
    sizePx: '16px',
    description: 'Equilíbrio perfeito de legibilidade e espaço'
  },
  large: {
    id: 'large',
    label: 'Confortável',
    shortLabel: 'A+',
    sizePx: '18px',
    description: 'Maior legibilidade para visualização em campo'
  }
};

interface FontSizeContextType {
  fontSize: FontSizeOption;
  setFontSize: (size: FontSizeOption) => void;
  fontDetails: FontSizeDetails;
  options: FontSizeDetails[];
}

const FontSizeContext = createContext<FontSizeContextType | undefined>(undefined);

export const FontSizeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [fontSize, setFontSizeState] = useState<FontSizeOption>(() => {
    try {
      const saved = safeStorage.getItem('rmax-font-size');
      if (saved === 'small' || saved === 'medium' || saved === 'large') {
        return saved;
      }
      return 'medium';
    } catch {
      return 'medium';
    }
  });

  const setFontSize = (size: FontSizeOption) => {
    setFontSizeState(size);
    try {
      safeStorage.setItem('rmax-font-size', size);
    } catch (e) {
      console.warn('Erro ao salvar preferência de fonte:', e);
    }
  };

  useEffect(() => {
    try {
      if (typeof document !== 'undefined' && document.documentElement) {
        const details = FONT_SIZE_OPTIONS[fontSize] || FONT_SIZE_OPTIONS.medium;
        document.documentElement.style.fontSize = details.sizePx;
        document.documentElement.setAttribute('data-font-size', fontSize);
      }
    } catch (e) {
      console.warn('Erro ao aplicar tamanho de fonte:', e);
    }
  }, [fontSize]);

  const fontDetails = FONT_SIZE_OPTIONS[fontSize] || FONT_SIZE_OPTIONS.medium;
  const options = Object.values(FONT_SIZE_OPTIONS);

  return (
    <FontSizeContext.Provider value={{ fontSize, setFontSize, fontDetails, options }}>
      {children}
    </FontSizeContext.Provider>
  );
};

export const useFontSize = () => {
  const context = useContext(FontSizeContext);
  if (context === undefined) {
    throw new Error('useFontSize must be used within a FontSizeProvider');
  }
  return context;
};

